/* @flow */

import type { Value } from "@crossroads/ui-components/src/components/Dropdown/context";
import type { LeaderboardItem } from "shop-state/types";

import React, { useContext, useState, useEffect } from "react";
import { useTranslate } from "@awardit/react-use-translate";
import cn from "classnames";

type LeaderboardItems = $ReadOnlyArray<LeaderboardItem>;

import { StoreInfoContext } from "entrypoint/shared";
import { useData, useSendMessage } from "crustate/react";
import { getLoadLeaderboardByDate, getLoadLeaderboardContent } from "state/leaderboard";
import Button from "components/Button";
import LeaderboardTable from "./LeaderboardTable";
import UserIcon from "@material-ui/icons/PersonOutlineRounded";
import StarIcon from "@material-ui/icons/StarOutlineRounded";
import CoinsBagIcon from "icons/coins-bag.svg";
import { Dropdown, DropdownItem } from "@crossroads/ui-components";

import {
  LeaderboardData,
  CustomerData,
  LeaderboardDistrictsData,
  LeaderboardContentData,
} from "data";

import styles from "./styles.scss";

type Props = {
  className?: string,
};

const LeaderboardWidget = ({ className }: Props): React$Node => {
  const sendMessage = useSendMessage();
  const { info, content: { leaderboardview, homeview } } = useContext(StoreInfoContext);
  const t = useTranslate();
  const customer = useData(CustomerData);

  const lb = useData(LeaderboardData);
  const lbDistrics = useData(LeaderboardDistrictsData);
  const lbContent = useData(LeaderboardContentData);
  const leaderboard: LeaderboardItems | null =
    lb.state === "LOADED" ? lb.data.leaderboard : null;
  const lbDistricsList = lbDistrics.state === "LOADED" ? lbDistrics.data : [];
  const lbRows = leaderboard !== null ? leaderboard.slice(0, 3) : [];
  const lbCount = lb.state === "LOADED" && lb.data.amountofwinners ? lb.data.amountofwinners.count : 0;
  const currentLeaderboardUser = lb.state === "LOADED" && lb.data.user ?
    lb.data.user : null;
  const currentLeaderboardTabelUser = leaderboard !== null && lb.state === "LOADED" ?
    leaderboard.find(item => item.name === lb.data.user.name) :
    null;
  const lbCurrentDistrict = lb.state === "LOADED" && lb.data.currentdistrict ?
    lb.data.currentdistrict : null;
  const lbContentData = lbContent.state === "LOADED" ? lbContent.data : null;
  const customerExtraData = customer.state === "LOGGED_IN" &&
    customer.data.awarditCustomerExtra !== null ?
    customer.data.awarditCustomerExtra.split(";") : null;

  const [currentDistrict, setCurrentDistrict] = useState<Value | null>(lbCurrentDistrict);
  const [pointsDeff, setPointsDeff] = useState<number | null>(null);

  const getYearLeaderboard = () => {
    const date = new Date();
    const fromDate = `${date.getFullYear()}-01-01`;
    const toDate = `${date.getFullYear()}-12-31`;

    sendMessage(getLoadLeaderboardByDate({
      fromdate: `${fromDate}`,
      todate: `${toDate}`,
      district: typeof currentDistrict === "string" ? currentDistrict : "",
    }));
  };

  useEffect(() => {
    if (lb.state === "LOADED" && currentLeaderboardUser) {
      if (lbCount > 0 && lbRows.length > 0) {
        setPointsDeff(lbRows[lbCount - 1].points -
          (currentLeaderboardUser.points !== null ? currentLeaderboardUser.points : 0));
      }
      else if (lbRows.length > 0) {
        setPointsDeff(lbRows[0].points -
          (currentLeaderboardUser.points !== null ? currentLeaderboardUser.points : 0));
      }
      else {
        setPointsDeff(0);
      }
    }

    sendMessage(getLoadLeaderboardContent({ district: typeof currentDistrict === "string" ? currentDistrict : "" }));
  }, [lb]);

  useEffect(() => {
    if (lb.state !== "LOADED") {
      return;
    }

    if (typeof currentDistrict === "string") {
      const { flags } = lb.data;
      if (flags && (flags.showdatesel > 0 && flags.showdatesel < 3)) {
        getYearLeaderboard();

        return;
      }

      sendMessage(getLoadLeaderboardByDate({ district: currentDistrict }));
    }
  }, [currentDistrict]);

  useEffect(() => {
    if (currentDistrict === null && lbDistricsList.length > 0) {
      const arr = lbDistricsList.filter(district => district.primary === true);
      if (arr.length !== 0) {
        const primaryDist = arr[0].id;
        setCurrentDistrict(primaryDist);
      }
    }
  }, [lbDistricsList]);

  const renderMissingInfoMessage = () => {
    const lbData = lb.state === "LOADED" ? lb.data : null;
    const excludedPartners = ["if_sek"];

    if (lbData === null || excludedPartners.includes(info.code)) {
      return null;
    }

    if (homeview.motivationLeaderboardWidgetIsActiveNameTable !== undefined &&
      homeview.motivationLeaderboardWidgetIsActiveNameTable !== null &&
      !homeview.motivationLeaderboardWidgetIsActiveNameTable &&
      lbData.user.points === 0 && lbData.user.position === 0) {
      return (
        <div className={cn(styles.widgetRow, styles.missingData)}>
          <div className={styles.missingDataInner}>
            <span>{t("LEADERBOARD.MISSING_USER_DATA")}</span>
          </div>
        </div>
      );
    }

    if (lb.state === "LOADED" && (leaderboard === null || leaderboard.length === 0)) {
      return (
        <div className={cn(styles.widgetRow, styles.missingData)}>
          <div className={styles.missingDataInner}>
            <span>{t("LEADERBOARD.MISSING_DATA")}</span>
          </div>
        </div>
      );
    }

    return null;
  };

  return lb.state === "LOADED" ? (
    <section className={cn(styles.widget, "awardit-leaderboardWidget", className)}>
      {homeview.motivationHeroTitle && (
        <div className={styles.widgetHeader}>
          <h2 className={styles.widgetTitle}>{homeview.motivationHeroTitle}</h2>
          {lb.data.currentdistrict && lbDistricsList.length > 1 && (
            <Dropdown
              name="leaderboardDistricts"
              className={cn(styles.dropdown, "awardit-leaderboardPageDistrictSelect")}
              placeholder={leaderboardview.showAmoutOfColumns ?? ""}
              value={currentDistrict !== null ? currentDistrict : lb.data.currentdistrict}
              onChange={val => setCurrentDistrict(val)}
            >
              {lbDistricsList.map(district => (
                <DropdownItem key={district.id} value={district.id}>
                  {district.label}
                </DropdownItem>
              ))}
            </Dropdown>
          )}
          {homeview.motivationHeroDescription && (
            /* eslint-disable react/no-danger */
            <div
              dangerouslySetInnerHTML={{ __html: homeview.motivationHeroDescription }}
              className={styles.motivationHeroDescription}
            />
            /* eslint-enable react/no-danger */
          )}
        </div>
      )}

      {renderMissingInfoMessage()}

      {lbContentData?.myPoints &&
        lb.data.user.points !== null &&
        currentLeaderboardUser && (
          <div className={styles.widgetRow}>
            <CoinsBagIcon className={styles.icon} />
            <span className={styles.description}>{lbContentData.myPoints}</span>
            <span className={styles.value}>{currentLeaderboardUser.points}</span>
          </div>
        )}
      {lbContentData?.myPosition &&
        lb.data.user.position !== null &&
        currentLeaderboardUser && (
          <div className={styles.widgetRow}>
            <UserIcon className={styles.icon} />
            <span className={styles.description}>{lbContentData.myPosition}</span>
            <span className={styles.value}>{currentLeaderboardUser.position}</span>
          </div>
        )}
      {lbContentData?.pointsToFirst &&
        lb.data.user.points !== null &&
        currentLeaderboardUser && (
          <div className={styles.widgetRow}>
            <StarIcon className={styles.icon} />
            <span className={styles.description}>{lbContentData.pointsToFirst}</span>
            <span className={styles.value}>{pointsDeff !== null && pointsDeff}</span>
          </div>
        )}
      {info.code === "if_sek" &&
        customer.state === "LOGGED_IN" &&
        customerExtraData !== null &&
        customerExtraData.length > 1 && (
        <>
          <div className={styles.widgetRow}>
            <UserIcon className={styles.icon} />
            <span className={styles.description}>Er försäkringsgrad</span>
            <span className={styles.value}>{customerExtraData[0]}</span>
          </div>
          <div className={styles.widgetRow}>
            <CoinsBagIcon className={styles.icon} />
            <span className={styles.description}>Ditt senaste utfall mot index</span>
            <span className={styles.value}>{customerExtraData[1]}</span>
          </div>
        </>
      )}
      {homeview.motivationLeaderboardWidgetIsActiveNameTable && (
        <LeaderboardTable
          currentLeaderboardUser={currentLeaderboardTabelUser}
          leaderboardRows={lbRows}
        />
      )}
      {homeview.motivationDescription && (
        <div className={styles.motivationDescription}>
          <p>{homeview.motivationDescription}</p>
        </div>
      )}
      {((homeview.motivationHeroButtonPrimaryText &&
        homeview.motivationHeroButtonPrimaryLink) ||
        (homeview.motivationHeroButtonSecondaryText &&
        homeview.motivationHeroButtonSecondaryLink)) && (
        <div className={styles.cta}>
          {homeview.motivationHeroButtonPrimaryText &&
            homeview.motivationHeroButtonPrimaryLink && (
            <Button to={homeview.motivationHeroButtonPrimaryLink} type="button" variant="primary" size="small">
              {homeview.motivationHeroButtonPrimaryText}
            </Button>
          )}
          {homeview.motivationHeroButtonSecondaryText &&
            homeview.motivationHeroButtonSecondaryLink && (
            <Button to={homeview.motivationHeroButtonSecondaryLink} type="button" variant="ghost" size="small">
              {homeview.motivationHeroButtonSecondaryText}
            </Button>
          )}
        </div>
      )}
    </section>
  ) : (
    <section className={cn(styles.widget, className)}>
      <div className={styles.dummy}>
        <p className={styles.title}>&nbsp;</p>
        <p>&nbsp;</p>
      </div>
    </section>
  );
};

export default LeaderboardWidget;
